import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Reminder, CreateReminderDto } from "../../types";
import { CreateReminderResponse } from "../../types/responses";
import { generateUUID } from "../../utils/uuidUtils";
import { API_ENDPOINTS, authenticatedFetch } from "../../config/api";

interface RemindersState {
  items: Reminder[];
  loading: boolean;
  error: string | null;
}

// Async thunk for fetching reminders via API
const fetchReminders = createAsyncThunk(
  "reminders/fetchReminders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.REMINDERS.GET_ALL
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch reminders: ${response.statusText}`);
      }

      const reminders: Reminder[] = await response.json();
      return reminders;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for creating a reminder via API
const createReminder = createAsyncThunk(
  "reminders/createReminder",
  async (reminderData: CreateReminderDto, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.REMINDERS.CREATE,
        {
          method: "POST",
          body: JSON.stringify(reminderData),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create reminder: ${response.statusText}`);
      }

      const result: CreateReminderResponse = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for updating a reminder via API
const updateReminderAsync = createAsyncThunk(
  "reminders/updateReminder",
  async (
    { id, updates }: { id: string; updates: Partial<CreateReminderDto> },
    { rejectWithValue }
  ) => {
    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.REMINDERS.UPDATE_BY_ID(id),
        {
          method: "PUT",
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update reminder: ${response.statusText}`);
      }

      const updatedReminder: Reminder = await response.json();
      return updatedReminder;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for deleting a reminder via API
const deleteReminderAsync = createAsyncThunk(
  "reminders/deleteReminder",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.REMINDERS.DELETE_BY_ID(id),
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete reminder: ${response.statusText}`);
      }

      return id;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

const initialState: RemindersState = {
  items: [],
  loading: false,
  error: null,
};

const remindersSlice = createSlice({
  name: "reminders",
  initialState,
  reducers: {
    addReminder: (
      state,
      action: PayloadAction<Omit<Reminder, "id" | "createdAt">>
    ) => {
      const newReminder: Reminder = {
        ...action.payload,
        id: generateUUID(),
        createdAt: new Date().toISOString(),
      };
      state.items.push(newReminder);
    },
    updateReminder: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Reminder> }>
    ) => {
      const { id, updates } = action.payload;
      const reminderIndex = state.items.findIndex(
        (reminder) => reminder.id === id
      );
      if (reminderIndex !== -1) {
        state.items[reminderIndex] = {
          ...state.items[reminderIndex],
          ...updates,
        };
      }
    },
    deleteReminder: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(
        (reminder) => reminder.id !== action.payload
      );
    },
    toggleReminder: (state, action: PayloadAction<string>) => {
      const reminder = state.items.find(
        (reminder) => reminder.id === action.payload
      );
      if (reminder) {
        reminder.completed = !reminder.completed;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearReminders: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReminders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReminders.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchReminders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createReminder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReminder.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createReminder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateReminderAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReminderAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(
          (reminder) => reminder.id === action.payload.id
        );
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateReminderAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteReminderAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteReminderAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(
          (reminder) => reminder.id !== action.payload
        );
      })
      .addCase(deleteReminderAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addReminder,
  updateReminder,
  deleteReminder,
  toggleReminder,
  setLoading,
  setError,
  clearReminders,
} = remindersSlice.actions;
export { fetchReminders, createReminder, updateReminderAsync, deleteReminderAsync };
export default remindersSlice.reducer;
